import { useStrictLocalization } from "@faire/web--source/common/localization";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";

import { CollectedCSAT } from "./CollectedCSAT";
import { OTHER_REASON } from "./csatReasons";
import { OpenReasonTextArea } from "./OpenReasonTextArea";
import { SubmitButton } from "./SubmitButton";
import { TellUsMore } from "./TellUsMore";
import { Feedback, ICSATContext } from "./types";
import { trackCsatSubmit } from "./utils";
import { YesNoButtons } from "./YesNoButtons";

// @ts-prune-ignore-next
export type IProps = ICSATContext & {
  visibleWhen?: boolean;
};

export const ComprehensiveCSATCollection: React.FC<IProps> = ({
  visibleWhen,
  ...context
}) => {
  const { strictLocalize } = useStrictLocalization();

  const [completed, setCompleted] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback | undefined>();
  const [checkedReasons, setCheckedReasons] = useState<string[]>([]);
  const [openReason, setOpenReason] = useState<string>("");

  if (visibleWhen === false) {
    return null;
  }

  if (completed) {
    return <CollectedCSAT />;
  }

  const handleYesNoResponse = (value: Feedback) => {
    setFeedback(value);
    if (value === "Yes") {
      setCompleted(true);
      trackCsatSubmit("Yes", [], "", context);
    }
  };

  const handleFormSubmission = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCompleted(true);
    trackCsatSubmit("No", checkedReasons, openReason, context);
  };

  const { brandOrRetailerToken, issue } = context;
  return (
    <>
      <YesNoButtons
        visibleWhen={feedback === undefined}
        value={feedback}
        onChange={handleYesNoResponse}
        brandOrRetailerToken={brandOrRetailerToken}
        issue={issue}
      />
      <CSATForm onSubmit={handleFormSubmission} visibleWhen={feedback === "No"}>
        <TellUsMore
          checkedReasons={checkedReasons}
          setCheckedReasons={setCheckedReasons}
        />
        <OpenReasonTextArea
          visibleWhen={checkedReasons.includes(OTHER_REASON(strictLocalize).id)}
          value={openReason}
          onChange={setOpenReason}
        />
        <SubmitButton
          disabled={isEmpty(checkedReasons) && isEmpty(openReason.trim())}
        />
      </CSATForm>
    </>
  );
};

interface ICSATFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  visibleWhen: boolean;
  children?: React.ReactNode;
}

const CSATForm: React.FC<ICSATFormProps> = ({
  visibleWhen,
  children,
  ...props
}) => {
  if (!visibleWhen) {
    return null;
  }

  return <form {...props}>{children}</form>;
};
